import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  inject
} from '@angular/core';
import { Subscription } from 'rxjs';

import { CarouselSlideDirective } from './carousel-slide.directive';
import { CarouselOptions, CarouselResponsiveOptions, CarouselSlidesOutputData } from './carousel.models';

@Component({
  selector: 'kh-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.less'],
  standalone: false
})
export class CarouselComponent implements AfterContentInit, OnChanges, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() public options?: CarouselOptions;
  @Input() public autoplay = true;
  @Input() public autoplayHoverPause = true;
  @Input() public autoplaySpeed = 3000;
  @Input() public autoplayTimeout = 6000;
  @Input() public carets = true;
  @Input() public dots = true;
  @Input() public dotsEach = true;
  @Input() public loop = true;
  @Input() public margin = 12;
  @Input() public nav = false;
  @Input() public navSpeed?: number;
  @Input() public navText: string[] = [];
  @Input() public responsive: Record<number, CarouselResponsiveOptions> = {
    0: {
      items: 1
    }
  };

  @Input() public rewind = false;
  @Input() public slideBy = 1;
  @Input() public stagePadding = 0;
  @Input() public items = 1;
  @Input() public dotsSpeed?: number;

  @Output() public translated = new EventEmitter<CarouselSlidesOutputData>();
  @Output() public initialized = new EventEmitter<CarouselSlidesOutputData>();
  @Output() public dotClicked = new EventEmitter<void>();

  @ContentChildren(CarouselSlideDirective) public slideTemplates!: QueryList<CarouselSlideDirective>;

  public slides: CarouselSlideDirective[] = [];
  public activeIndex = 0;
  public visibleItems = 1;
  public effectiveSlideBy = 1;
  public transitionMs = 500;
  public isHovered = false;
  public isDragging = false;
  public dragOffsetPx = 0;

  private autoplayTimerId: number | null = null;
  private slidesChangesSubscription: Subscription | null = null;
  private contentInitialized = false;
  private hasEmittedInitialized = false;
  private dragPointerId: number | null = null;
  private dragCaptureTarget: HTMLElement | null = null;
  private dragStartCoord = 0;
  private suppressClick = false;
  private readonly dragActivatePx = 6;
  private readonly dragThresholdPx = 40;
  private readonly suppressClickIfDragged = (event: Event): void => {
    if (!this.suppressClick) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.suppressClick = false;
  };

  public ngAfterContentInit(): void {
    this.contentInitialized = true;
    this.rebuildSlides();
    this.slidesChangesSubscription?.unsubscribe();
    this.slidesChangesSubscription = this.slideTemplates.changes.subscribe(() => {
      this.rebuildSlides();
    });
    this.elementRef.nativeElement.addEventListener('click', this.suppressClickIfDragged, true);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.applyOptionsForViewport();
    this.clampActiveIndex();
    this.restartAutoplay();

    if (this.contentInitialized) {
      this.emitTranslated();
    }
  }

  public ngOnDestroy(): void {
    this.slidesChangesSubscription?.unsubscribe();
    this.stopAutoplay();
    this.resetDrag();
    this.elementRef.nativeElement.removeEventListener('click', this.suppressClickIfDragged, true);
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  public onWindowPointerUp(event: PointerEvent): void {
    if (this.dragPointerId === event.pointerId) {
      this.onPointerUp(event);
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.applyOptionsForViewport();
    this.clampActiveIndex();

    if (this.contentInitialized) {
      this.emitTranslated();
    }
  }

  public onMouseEnter(): void {
    this.isHovered = true;
  }

  public onMouseLeave(): void {
    this.isHovered = false;
  }

  public previous(): void {
    const nextIndex = this.activeIndex - this.effectiveSlideBy;

    if (this.activeIndex > 0) {
      this.activeIndex = Math.max(0, nextIndex);
      this.emitTranslated();
      return;
    }

    if (this.isLoopEnabled || this.isRewindEnabled) {
      this.activeIndex = this.maxStartIndex;
      this.emitTranslated();
    }
  }

  public next(): void {
    const nextIndex = this.activeIndex + this.effectiveSlideBy;

    if (this.activeIndex < this.maxStartIndex) {
      this.activeIndex = Math.min(nextIndex, this.maxStartIndex);
      this.emitTranslated();
      return;
    }

    if (this.isLoopEnabled || this.isRewindEnabled) {
      this.activeIndex = 0;
      this.emitTranslated();
    }
  }

  public goToDot(dotIndex: number): void {
    const dotsEach = this.isDotsEachEnabled;
    const targetIndex = dotsEach ? dotIndex : dotIndex * this.effectiveSlideBy;

    this.activeIndex = Math.min(targetIndex, this.maxStartIndex);

    this.dotClicked.emit();
    this.emitTranslated();
  }

  public onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    if (this.itemCount <= 1 || this.dragPointerId !== null) {
      return;
    }

    this.dragPointerId = event.pointerId;
    this.dragCaptureTarget = event.currentTarget as HTMLElement;
    this.dragStartCoord = event.clientX;
    this.dragOffsetPx = 0;
    this.isDragging = false;
    this.stopAutoplay();
  }

  public onPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const delta = event.clientX - this.dragStartCoord;

    if (!this.isDragging && Math.abs(delta) < this.dragActivatePx) {
      return;
    }

    if (!this.isDragging && this.dragCaptureTarget) {
      this.dragCaptureTarget.setPointerCapture(event.pointerId);
    }

    this.isDragging = true;
    this.dragOffsetPx = delta;
    event.preventDefault();
    this.changeDetectorRef.markForCheck();
  }

  public onPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const offset = this.dragOffsetPx;
    const didDrag = this.isDragging;
    const target = this.dragCaptureTarget ?? (event.currentTarget as HTMLElement | null);

    if (target && typeof target.hasPointerCapture === 'function' && target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }

    this.resetDrag();

    if (didDrag && Math.abs(offset) >= this.dragThresholdPx) {
      this.suppressClick = true;
      if (offset < 0) {
        this.next();
      }
      else {
        this.previous();
      }
    }

    this.restartAutoplay();
    this.changeDetectorRef.markForCheck();
  }

  public get stageTransform(): string {
    const percentageOffset = (this.activeIndex * 100) / this.visibleItems;
    const gapOffsetPx = (this.activeIndex * this.itemGapPx) / this.visibleItems;
    const dragOffset = this.dragOffsetPx;
    const axisOffset = dragOffset === 0
      ? `calc(-${percentageOffset}% - ${gapOffsetPx}px)`
      : `calc(-${percentageOffset}% - ${gapOffsetPx}px + ${dragOffset}px)`;

    return `translate3d(${axisOffset}, 0, 0)`;
  }

  public get stageTransitionMs(): number {
    return this.isDragging ? 0 : this.transitionMs;
  }

  public get itemFlexBasis(): string {
    const basisPercent = 100 / this.visibleItems;

    if (this.visibleItems <= 1 || this.itemGapPx <= 0) {
      return `${basisPercent}%`;
    }

    const totalVisibleGapPx = this.itemGapPx * (this.visibleItems - 1);
    const itemGapSharePx = totalVisibleGapPx / this.visibleItems;
    return `calc(${basisPercent}% - ${itemGapSharePx}px)`;
  }

  public get itemGapPx(): number {
    return this.configuredMargin ?? 0;
  }

  public get stagePaddingPx(): number {
    return this.configuredStagePadding ?? 0;
  }

  public get showNav(): boolean {
    if (!this.isNavEnabled || !this.isCaretsEnabled) {
      return false;
    }

    return this.itemCount > this.visibleItems;
  }

  public get showDots(): boolean {
    if (!this.isDotsEnabled) {
      return false;
    }

    return this.itemCount > 1;
  }

  public get dotIndexes(): number[] {
    const dotsEach = this.isDotsEachEnabled;

    if (dotsEach) {
      return Array.from({ length: this.itemCount }, (_, i) => i);
    }

    const pageCount = Math.max(1, Math.ceil((this.maxStartIndex + 1) / this.effectiveSlideBy));
    return Array.from({ length: pageCount }, (_, i) => i);
  }

  public isDotActive(dotIndex: number): boolean {
    const dotsEach = this.isDotsEachEnabled;

    if (dotsEach) {
      return this.activeIndex === dotIndex;
    }

    return Math.floor(this.activeIndex / this.effectiveSlideBy) === dotIndex;
  }

  public canGoPrevious(): boolean {
    if (this.isLoopEnabled || this.isRewindEnabled) {
      return this.itemCount > 1;
    }

    return this.activeIndex > 0;
  }

  public canGoNext(): boolean {
    if (this.isLoopEnabled || this.isRewindEnabled) {
      return this.itemCount > 1;
    }

    return this.activeIndex < this.maxStartIndex;
  }

  public get prevNavText(): string {
    return this.configuredNavText?.[0] ?? '&#8249;';
  }

  public get nextNavText(): string {
    return this.configuredNavText?.[1] ?? '&#8250;';
  }

  public get itemCount(): number {
    return this.slides.length;
  }

  public get maxStartIndex(): number {
    return Math.max(0, this.itemCount - Math.ceil(this.visibleItems));
  }

  private resetDrag(): void {
    this.dragPointerId = null;
    this.dragCaptureTarget = null;
    this.dragStartCoord = 0;
    this.dragOffsetPx = 0;
    this.isDragging = false;
  }

  private rebuildSlides(): void {
    this.slides = this.slideTemplates?.toArray() ?? [];
    this.applyOptionsForViewport();
    this.clampActiveIndex();
    this.restartAutoplay();
    const payload = this.buildOutputData();

    if (!this.hasEmittedInitialized) {
      this.hasEmittedInitialized = true;
      this.initialized.emit(payload);
    }

    this.translated.emit(payload);
    this.changeDetectorRef.markForCheck();
  }

  private applyOptionsForViewport(): void {
    const responsive = this.getResponsiveOptions();
    const configuredItems = responsive.items ?? this.configuredItems ?? 1;
    const configuredSlideBy = responsive.slideBy ?? this.configuredSlideBy ?? 1;

    this.visibleItems = this.normalizeVisibleItems(configuredItems);
    const configuredStep = Math.max(1, Math.floor(configuredSlideBy));
    this.effectiveSlideBy = configuredStep;

    const speed = this.configuredNavSpeed ?? this.configuredDotsSpeed ?? 500;
    this.transitionMs = Math.max(0, speed);
  }

  private normalizeVisibleItems(value: number): number {
    if (!Number.isFinite(value)) {
      return 1;
    }

    const rounded = Math.round(value);
    const normalized = Math.abs(value - rounded) < 0.01 ? rounded : value;
    return Math.max(1, normalized);
  }

  private clampActiveIndex(): void {
    if (this.activeIndex > this.maxStartIndex) {
      this.activeIndex = this.maxStartIndex;
    }

    if (this.activeIndex < 0) {
      this.activeIndex = 0;
    }
  }

  private getResponsiveOptions(): { items?: number; slideBy?: number } {
    const responsive = this.configuredResponsive;

    if (!responsive) {
      return {};
    }

    const viewportWidth = typeof window === 'undefined' ? 1920 : window.innerWidth;
    const breakpoints = Object.keys(responsive)
      .map((key) => Number(key))
      .filter((breakpoint) => !Number.isNaN(breakpoint))
      .sort((a, b) => a - b);

    let selected: { items?: number; slideBy?: number } = {};
    breakpoints.forEach((breakpoint) => {
      if (viewportWidth >= breakpoint) {
        selected = responsive[breakpoint] ?? selected;
      }
    });

    return selected;
  }

  private restartAutoplay(): void {
    this.stopAutoplay();

    if (!this.isAutoplayEnabled || this.itemCount <= 1) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const timeoutMs = Math.max(1000, this.configuredAutoplayTimeout ?? 5000);

    this.autoplayTimerId = window.setInterval(() => {
      if (this.isAutoplayHoverPauseEnabled && this.isHovered) {
        return;
      }

      this.next();
    }, timeoutMs);
  }

  private stopAutoplay(): void {
    if (this.autoplayTimerId === null) {
      return;
    }

    if (typeof window === 'undefined') {
      this.autoplayTimerId = null;
      return;
    }

    window.clearInterval(this.autoplayTimerId);
    this.autoplayTimerId = null;
  }

  private emitTranslated(): void {
    this.translated.emit(this.buildOutputData());
    this.changeDetectorRef.markForCheck();
  }

  private buildOutputData(): CarouselSlidesOutputData {
    return {
      startPosition: this.activeIndex,
      slides: this.slides.map((slide, index) => ({ id: slide.id || `${index}` }))
    };
  }

  private get isAutoplayEnabled(): boolean {
    return this.options?.autoplay ?? this.autoplay;
  }

  private get isAutoplayHoverPauseEnabled(): boolean {
    return this.options?.autoplayHoverPause ?? this.autoplayHoverPause;
  }

  private get configuredAutoplayTimeout(): number {
    return this.options?.autoplayTimeout ?? this.autoplayTimeout;
  }

  private get isDotsEnabled(): boolean {
    return this.options?.dots ?? this.dots;
  }

  private get isCaretsEnabled(): boolean {
    return this.options?.carets ?? this.carets;
  }

  private get isDotsEachEnabled(): boolean {
    return this.options?.dotsEach ?? this.dotsEach;
  }

  private get configuredDotsSpeed(): number | undefined {
    return this.options?.dotsSpeed ?? this.dotsSpeed;
  }

  private get configuredItems(): number {
    return this.options?.items ?? this.items;
  }

  private get isLoopEnabled(): boolean {
    return this.options?.loop ?? this.loop;
  }

  private get configuredMargin(): number {
    return this.options?.margin ?? this.margin;
  }

  private get isNavEnabled(): boolean {
    return this.options?.nav ?? this.nav;
  }

  private get configuredNavSpeed(): number | undefined {
    return this.options?.navSpeed ?? this.navSpeed;
  }

  private get configuredNavText(): string[] {
    return this.options?.navText ?? this.navText;
  }

  private get configuredResponsive(): Record<number, CarouselResponsiveOptions> {
    return this.options?.responsive ?? this.responsive;
  }

  private get isRewindEnabled(): boolean {
    return this.options?.rewind ?? this.rewind;
  }

  private get configuredSlideBy(): number {
    return this.options?.slideBy ?? this.slideBy;
  }

  private get configuredStagePadding(): number {
    return this.options?.stagePadding ?? this.stagePadding;
  }
}
